import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import AppLayout from '../../component/appLayout';
import {
  CUSTOMER_MEMBER_ROLE,
  INVOICE_TYPE,
  BUTTON_TYPE,
  TAB_BUTTON_TYPE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  EMPTY_STATE_SIZE,
  INVOICE_STATUS_TYPE,
  INVOICE_STATUS,
  InvoiceDraftMoreMenuItem,
  InvoiceMoreMenuItems,
  MENU_OPTIONS,
  ICON_SIZE
} from '../../global/constants';
import { currencySymbols } from '../../global/row';
import { calculateDueTime, getFormattedDate, getUrlParam, isEmpty } from '../../helpers/common';
import { deleteInvoice, getInvoiceList, getInvoiceViewData } from '../../services/invoiceService';
import { Content, Heading, Title } from '../project/allProjects/header/styles';
import {
  Pagedata,
  HeaderBlock,
  Group,
  ArrowBtn,
  Dropbox,
  Downarrow,
  Pagination,
  LeftDiv,
  ShowRow,
  RowText,
  RightDiv,
  Text,
  Buttons,
  HeaderLeft,
  Icon,
  Table,
  TableHead,
  TableHeader,
  TableHeadGroup,
  TableWrapper,
  TableRow,
  TableData,
  TableDataGroup,
  CompanyName,
  CustomerName,
  CompanyDetails,
  InvoiceNumber,
  OptionMenu
} from './styles';
import { getCustomerList, getCustomerProjectDetail } from '../../services/customerServices';
import DeleteModal from '../../component/modals/deleteModal';
import InvoiceLoading from '../../component/loading/invoiceLoading';
import Button from '../../component/button';
import Modal from '../../component/modals/modal';
import {
  duplicateInputInvoiceInterface,
  InvoiceListFilteredWiseInterface,
  projectIdInterface
} from '../../interfaces/InvoiceInterface';
import { captureException } from '../../services/logService';
import { trackAnalyticActivity } from '../../services/analyticsService';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { INVOICE_ANALYTICS } from '../../global/analyticsConstants';
import { useMobileDevice } from '../../global/useMobile';
import { Dropdown, DropdownItem } from '../../component/dropdown';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import AddInvoiceModal from '../../component/modals/addInvoiceModal';
import ResponsiveHeader from '../../component/responsiveHeader';
import BottomSubNavigation from '../../component/bottomNavigation/subNavigation';
import BottomNavigationItem from '../../component/bottomNavigation/subNavigation/bottomNavigationItem';
import Tabs from '../../component/tabs';
import IconButton from '../../component/button/iconButton';
import EmptyStateV2 from '../../component/emptyState';
import { ROUTES } from '../../routes/urls';

const Invoices: React.FC = () => {
  //States
  const [isOpen, setIsOpen] = useState(false);
  const [customerListIndex, setCustomerListIndex] = useState({ start: 1, end: 10 });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState<InvoiceListFilteredWiseInterface | null>(null);
  const [deleteModelOpen, setDeleteModalOpen] = useState(false);
  const [openData, setOpenData] = useState<InvoiceListFilteredWiseInterface[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceListFilteredWiseInterface[]>([]);
  const [invoiceListFilteredWise, setInvoiceListFilteredWise] = useState<InvoiceListFilteredWiseInterface[]>([]);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  // mobx store variable
  const { workspaceStore, invoiceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { invoices } = invoiceStore;
  const { id: workspace_id, customerRole, isOwner, isAdmin } = workspace || {};

  //Other variable
  const history = useHistory();
  const existingPageData = UserPreferenceSingleton.getInstance().getInvoicePagination() as
    | Record<string, number>
    | undefined;
  const storedRowsPerPage = existingPageData?.[workspace_id || ''];
  const [pageData, setPageData] = useState(() => {
    return {
      currentPage: 1,
      numOfData: storedRowsPerPage ? Number(storedRowsPerPage) : 100
    };
  });
  const filterType: number = Number(getUrlParam(history.location.search, 'type'));
  const invoicesData = filterType === INVOICE_TYPE.ALL ? invoices : openData;
  const rowOptions = [10, 20, 50, 100];
  const isMobile = useMobileDevice();

  // manage pagination data
  const handleIndexChange = useCallback(
    (currentPage: number, numOfData: number) => {
      const startIndex = Math.min((currentPage - 1) * numOfData + 1, invoicesData?.length ?? 0);
      const endIndex = Math.min(currentPage * numOfData, invoicesData?.length ?? 0);
      setCustomerListIndex({ start: startIndex, end: endIndex });
    },
    [invoicesData?.length]
  );

  // load initial data
  const loadData = useCallback(
    async () => {
      try {
        if (!isEmpty(workspace_id)) {
          setLoading(true);
          if (storedRowsPerPage && pageData.numOfData !== Number(storedRowsPerPage)) {
            setPageData({
              ...pageData,
              numOfData: Number(storedRowsPerPage)
            });
          }
          handleIndexChange(pageData?.currentPage, pageData?.numOfData);
          if (workspace?.isAdmin || workspace?.isOwner) await getCustomerList();
          const result = await getInvoiceList(workspace);
          let updatedList;
          const startingIndex = pageData?.numOfData * (pageData?.currentPage - 1);
          if (filterType === INVOICE_TYPE.ALL) {
            updatedList = result;
            const subData = updatedList?.slice(startingIndex, startingIndex + pageData?.numOfData);
            setFilteredInvoices(subData);
            setInvoiceListFilteredWise(updatedList);
          } else {
            updatedList = result?.filter((item: InvoiceListFilteredWiseInterface) => item?.statusText !== 'PAID');
            setOpenData(updatedList);
            const subData = updatedList?.slice(startingIndex, startingIndex + pageData?.numOfData);
            setFilteredInvoices(subData);
            setInvoiceListFilteredWise(updatedList);
          }
          loadPaginationData(pageData?.currentPage, pageData?.numOfData, updatedList);
        }
      } catch (error) {
        captureException(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace_id, storedRowsPerPage, pageData, handleIndexChange]
  );

  // manage invoice type
  const trackActivity = useCallback(() => {
    if (filterType === INVOICE_TYPE.ALL) {
      trackAnalyticActivity(INVOICE_ANALYTICS.VIEW_LIST, { view: 'all' });
    } else {
      trackAnalyticActivity(INVOICE_ANALYTICS.VIEW_LIST, { view: 'open' });
    }
  }, [filterType]);

  useEffect(() => {
    loadData();
    trackActivity();
  }, [loadData, trackActivity]);

  useEffect(() => {
    return () => {
      setInvoiceListFilteredWise([]);
      setFilteredInvoices([]);
    };
  }, []);

  // handle click event for new invoice
  const handleClick = useCallback(() => {
    trackAnalyticActivity(INVOICE_ANALYTICS.NEW_INVOICE_CLICKED);
    setIsOpen(!isOpen);
  }, [isOpen]);

  // open delete model
  const openDeleteModel = useCallback((item: InvoiceListFilteredWiseInterface) => {
    setItem(item);
    setDeleteModalOpen(true);
  }, []);

  // close delete model
  const closeDeleteModel = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  // edit invoice
  const onInvoiceEdit = useCallback(
    async (item: InvoiceListFilteredWiseInterface) => {
      const response = await Promise.all([getInvoiceViewData(item?.id)]);
      if (item?.statusText === 'DRAFT') {
        trackAnalyticActivity(INVOICE_ANALYTICS.VIEW_DRAFT);
      }
      rootStore.invoiceStore.setInvoiceDetail(response[0]);
      history.push(`${ROUTES.EDIT_INVOICES.replace(':invoice_id', item?.id)}?customer=${item?.customer?.id}`);
    },
    [history]
  );

  // delete invoice
  const onClickDeleteInvoice = useCallback(
    async (item: { id: string }) => {
      try {
        setLoading(true);
        const result = await deleteInvoice(item?.id);
        if (result) {
          setDeleteModalOpen(false);
          trackAnalyticActivity(INVOICE_ANALYTICS.DELETED);
          setIsOpen(false);
          rootStore.alertNotificationStore.setSuccessMessage('Invoice deleted successfully!');
          loadData();
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoading(false);
      }
    },
    [loadData]
  );

  // load pagination data
  const loadPaginationData = useCallback(
    (currentPage: number, numOfData: number, list?: InvoiceListFilteredWiseInterface[]) => {
      if (!list) {
        list = invoiceListFilteredWise;
      }
      const startingIndex = numOfData * (currentPage - 1);
      const endingIndex =
        startingIndex + numOfData > invoicesData?.length ? invoicesData?.length : startingIndex + numOfData;
      const subData = invoicesData?.slice(startingIndex, endingIndex);
      if (subData?.length > 0) {
        setFilteredInvoices(subData);
      } else {
        setFilteredInvoices(list || []);
      }
    },
    [invoicesData, invoiceListFilteredWise]
  );

  //  filter list of invoices on the basis of tab selected
  const onClickTab = useCallback(
    (key?: number) => {
      let updatedList;
      if (key === INVOICE_TYPE.ALL) {
        history.push(`${history.location.pathname}?type=${INVOICE_TYPE.ALL}`);
        updatedList = invoicesData;
      } else {
        history.push(`${history.location.pathname}?type=${INVOICE_TYPE.OPEN}`);
        updatedList = invoicesData?.filter((item) => item?.statusText !== 'PAID');
      }
      setPageData((prevData) => ({
        ...(prevData || {}),
        currentPage: 1
      }));
      setInvoiceListFilteredWise(updatedList);
      loadPaginationData(1, pageData?.numOfData, updatedList);
    },
    [invoicesData, pageData, history, loadPaginationData]
  );

  // redirect to invoice details page
  const onClickInvoice = useCallback(
    (invoice: InvoiceListFilteredWiseInterface) => {
      history.push(ROUTES.INVOICE_DETAILS.replace(':id', invoice?.id));
    },
    [history]
  );

  // handle close model
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // for make duplicate invoice
  const onDuplicateInvoice = useCallback(
    async (data: InvoiceListFilteredWiseInterface) => {
      try {
        if (!workspace_id) return;
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const projectResult = await getCustomerProjectDetail(workspace_id, data?.customer?.id);
        const updatedItem = data?.items?.map((item) => {
          if (!isEmpty(item?.projectId)) {
            const project = projectResult?.find((ele: { id: projectIdInterface }) => ele?.id === item?.projectId);
            return { ...item, projectId: project };
          } else {
            return item;
          }
        });
        const invoiceData: duplicateInputInvoiceInterface = {
          ...data,
          currency: data?.currency,
          date: new Date().toISOString(),
          userId: userDetails?.id,
          customerId: data?.customer?.id,
          emails: [],
          items: updatedItem,
          isSendMeCopy: false,
          dueDate: data?.dueDays ? new Date(new Date().setDate(new Date().getDate() + data.dueDays)).toISOString() : '',
          dueDays: data.dueDays.toString()
        };
        const fieldsToDelete: (keyof duplicateInputInvoiceInterface)[] = [
          'id',
          'status',
          'paidAmount',
          'statusText',
          'number',
          'invoicePayment',
          'customer',
          'dueAmount'
        ];
        fieldsToDelete.forEach((field) => delete invoiceData[field]);
        rootStore.invoiceStore.setInvoiceDetail(invoiceData);
        history.push(`${ROUTES.ADD_NEW_INVOICES_2}?customer=${data?.customer?.id}`);
      } catch (error) {
        captureException(error);
      }
    },
    [workspace_id, history]
  );

  // handle dropdown click
  const handleDropdownClick = useCallback(
    (key: number | string, item: InvoiceListFilteredWiseInterface) => {
      key = Number(key);
      setSelectedItem(null);
      if (key === MENU_OPTIONS.VIEW) {
        onClickInvoice(item);
      } else if (key === MENU_OPTIONS.EDIT) {
        onInvoiceEdit(item);
      } else if (key === MENU_OPTIONS.DELETE) {
        openDeleteModel(item);
      } else if (key === MENU_OPTIONS.DUPLICATE) {
        onDuplicateInvoice(item);
      }
    },
    [onClickInvoice, onDuplicateInvoice, onInvoiceEdit, openDeleteModel]
  );

  // handle menu click for rows per page
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const rowsPerPage = parseInt(key);
      const pageData = {
        ...(existingPageData || {}),
        [workspace_id || '']: rowsPerPage
      };
      UserPreferenceSingleton.getInstance().setInvoicePagination(pageData);
      setPageData((prevPageData) => {
        // Recalculate current page based on the new number of rows per page
        const newPage = Math.floor(((prevPageData.currentPage - 1) * prevPageData.numOfData) / rowsPerPage) + 1;
        const updatedPageData = { ...prevPageData, currentPage: newPage, numOfData: rowsPerPage };
        handleIndexChange(newPage, rowsPerPage);
        loadPaginationData(newPage, rowsPerPage);
        return updatedPageData;
      });
    },
    [existingPageData, handleIndexChange, loadPaginationData, workspace_id]
  );

  // for redirect to forward and backward page
  const onChangePage = useCallback(
    (direction: 'Forward' | 'Backward') => {
      setPageData((prevPageData) => {
        let updatedPage = prevPageData.currentPage;
        if (
          direction === 'Forward' &&
          prevPageData.currentPage * prevPageData.numOfData < (invoicesData?.length ?? 0)
        ) {
          updatedPage += 1;
        } else if (direction === 'Backward' && prevPageData.currentPage > 1) {
          updatedPage -= 1;
        }
        handleIndexChange(updatedPage, prevPageData.numOfData);
        loadPaginationData(updatedPage, prevPageData.numOfData);
        return { ...prevPageData, currentPage: updatedPage };
      });
    },
    [handleIndexChange, invoicesData?.length, loadPaginationData]
  );

  // handle dropdown open
  const handleDropdownOpen = useCallback(() => {
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen]);

  // render more menu items
  const renderMoreMenuItems = useMemo(
    () => (item: InvoiceListFilteredWiseInterface) => {
      const selectedMenuItems = item?.status === INVOICE_STATUS.DRAFT ? InvoiceDraftMoreMenuItem : InvoiceMoreMenuItems;
      return (
        <>
          {selectedMenuItems.map((menuItem) => (
            <DropdownItem
              key={menuItem.key}
              label={menuItem.label}
              onClick={() => handleDropdownClick(menuItem.key, item)}
              tone={menuItem.label === 'Delete' ? 'critical' : 'primary'}
            />
          ))}
        </>
      );
    },
    [handleDropdownClick]
  );

  // handle customer dropdown open/close
  const handleInvoiceDropdownToggle = useCallback((invoiceId: string, isOpen: boolean) => {
    setActiveDropdownId(isOpen ? invoiceId : null);
  }, []);

  // get filtered invoices based on the filter type
  const filteredInvoiceList = useMemo(() => {
    if (!filteredInvoices?.length) return null;
    return (
      <TableWrapper>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>
                <p>Name</p>
              </TableHeader>
              <TableHeadGroup>
                <TableHeader>
                  <p>Invoice no</p>
                </TableHeader>
                <TableHeader>
                  <p>Invoice Date</p>
                </TableHeader>
                <TableHeader>
                  <p>Amount</p>
                </TableHeader>
                <TableHeader>
                  <p>Status</p>
                </TableHeader>
              </TableHeadGroup>
              <TableHeader></TableHeader>
            </tr>
          </TableHead>
          <tbody>
            {filteredInvoices?.map((item, index) => {
              const ref = currencySymbols.find((x) => x.code === item?.currency);
              const symbol = ref?.symbol;
              return (
                <TableRow role='button' isActive={selectedItem === item?.id} onClick={() => onClickInvoice(item)}>
                  <TableData>
                    <CompanyDetails>
                      {!isEmpty(item?.customer?.firstName) && !isEmpty(item?.customer?.lastName) && (
                        <CustomerName>
                          {item?.customer?.firstName} {item?.customer?.lastName}
                        </CustomerName>
                      )}
                      <CompanyName>{item?.customer?.companyName}</CompanyName>
                    </CompanyDetails>
                  </TableData>
                  <TableDataGroup>
                    <TableData>
                      <InvoiceNumber>{item?.number}</InvoiceNumber>
                    </TableData>
                    <TableData>
                      <CompanyDetails>
                        <CustomerName>{getFormattedDate(new Date(item?.date))}</CustomerName>
                        <CompanyName>{calculateDueTime(item?.dueDate)?.dueTime}</CompanyName>
                      </CompanyDetails>
                    </TableData>
                    <TableData>
                      <CompanyDetails>
                        <CustomerName>
                          {symbol} {item?.totalAmount}
                        </CustomerName>
                        {item?.status !== INVOICE_STATUS_TYPE.PAID && (
                          <CompanyName>
                            Due {symbol} {item?.dueAmount}
                          </CompanyName>
                        )}
                      </CompanyDetails>
                    </TableData>
                    <TableData>
                      <InvoiceNumber status={item?.statusText}>{item?.statusText}</InvoiceNumber>
                    </TableData>
                  </TableDataGroup>
                  <TableData>
                    {customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS && (isAdmin || isOwner) && (
                      <Dropdown
                        modalTitle='Manage Invoice'
                        activeClassName='active'
                        content={
                          <OptionMenu className={activeDropdownId === item?.id ? 'active-state' : ''}>
                            <IconButton
                              iconName='three-dots-horizontal-icon'
                              size={isMobile ? ICON_BUTTON_SIZE.MEDIUM : ICON_BUTTON_SIZE.SMALL}
                              variant={ICON_BUTTON_TYPE.TERTIARY}
                              onClick={() => setSelectedItem((prev) => (prev === null ? item?.id : null))}
                              className='invoice-more-icon'
                            />
                          </OptionMenu>
                        }
                        trigger='click'
                        onOutsideClick={() => setSelectedItem(null)}
                        updateDropdownOpen={(isOpen) => handleInvoiceDropdownToggle(item?.id, isOpen)}>
                        {renderMoreMenuItems(item)}
                      </Dropdown>
                    )}
                  </TableData>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </TableWrapper>
    );
  }, [
    activeDropdownId,
    customerRole,
    filteredInvoices,
    handleInvoiceDropdownToggle,
    isAdmin,
    isMobile,
    isOwner,
    onClickInvoice,
    renderMoreMenuItems,
    selectedItem
  ]);

  // Renders the tab navigation menu for filtering invoices
  const renderTabOption = useCallback(() => {
    // tab options for invoice filtering
    const tabOptions = [
      {
        key: INVOICE_TYPE.OPEN,
        label: 'Open',
        isActive: filterType === INVOICE_TYPE.OPEN || !filterType
      },
      {
        key: INVOICE_TYPE.ALL,
        label: 'All',
        isActive: filterType === INVOICE_TYPE.ALL
      }
    ];
    return (
      <Buttons>
        {tabOptions.map((tab) => (
          <Tabs
            key={tab.key}
            title={tab.label}
            variant={TAB_BUTTON_TYPE.STANDARD}
            size='small'
            isActive={tab.isActive}
            onClick={() => onClickTab(tab.key)}
          />
        ))}
      </Buttons>
    );
  }, [filterType, onClickTab]);

  return (
    <>
      <AppLayout>
        <Group className='desktop-content'>
          <Pagedata>
            {!isMobile ? (
              <Content>
                <HeaderBlock>
                  <HeaderLeft>
                    <Heading>
                      <Icon>
                        <SVGIcon
                          name='invoice-icon'
                          width={ICON_SIZE.LG}
                          height={ICON_SIZE.LG}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--brand-primary)'
                        />
                      </Icon>
                      <Title>Invoices</Title>
                    </Heading>
                  </HeaderLeft>
                  {!isMobile && renderTabOption()}
                </HeaderBlock>
                {filteredInvoices?.length > 0 &&
                  customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS &&
                  (isAdmin || isOwner) && (
                    <Button
                      title='New invoice'
                      variant={BUTTON_TYPE.PRIMARY}
                      onClick={handleClick}
                      iconName='plus-icon'
                      buttonType='button'
                      iconPosition='start'
                      size='small'
                    />
                  )}
              </Content>
            ) : (
              <ResponsiveHeader title='Invoices' />
            )}
            {loading && (filteredInvoices?.length === 0 || !filteredInvoices) && <InvoiceLoading />}
            {!loading && filteredInvoices?.length === 0 && (
              <EmptyStateV2
                size={EMPTY_STATE_SIZE.MEDIUM}
                title={'You have no invoice records'}
                description={'You can create new invoice by clicking the button below.'}
                imageUrl={'invoice_empty'}
                buttonTitle={'New invoice'}
                onClick={handleClick}
                isAlignedCenter={true}
              />
            )}
            {isMobile && renderTabOption()}
            {filteredInvoiceList}
          </Pagedata>
          {filteredInvoices?.length > 0 && (
            <Pagination>
              <LeftDiv>
                <Text>Show</Text>
                <Dropdown
                  modalTitle='Show'
                  isMinWidth={110}
                  onOutsideClick={() => setDropdownOpen(false)}
                  content={
                    <ShowRow role='button' onClick={() => handleDropdownOpen()}>
                      <RowText>{`${pageData.numOfData} Rows`}</RowText>
                      <Downarrow>
                        <SVGIcon
                          name='down-arrow-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-secondary)'
                          className={dropdownOpen ? 'arrow-open' : ''}
                        />
                      </Downarrow>
                    </ShowRow>
                  }
                  trigger='click'>
                  <Dropbox>
                    {rowOptions.map((rows) => (
                      <DropdownItem
                        key={rows}
                        label={`${rows} Rows`}
                        onClick={() => handleMenuClick({ key: String(rows) })}
                      />
                    ))}
                  </Dropbox>
                </Dropdown>
              </LeftDiv>
              <RightDiv>
                <Text>{`${customerListIndex?.start} - ${
                  customerListIndex?.end > invoicesData?.length ? invoicesData?.length : customerListIndex?.end
                } of ${invoicesData?.length}`}</Text>
                <ArrowBtn>
                  <IconButton
                    iconName='new-back-arrow-icon'
                    size={ICON_BUTTON_SIZE.LARGE}
                    variant={isMobile ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
                    className='left-arrow-icon'
                    type='rounded'
                    onClick={() => onChangePage('Backward')}
                  />
                  <IconButton
                    iconName='updated-right-arrow-icon'
                    size={ICON_BUTTON_SIZE.LARGE}
                    variant={isMobile ? ICON_BUTTON_TYPE.TERTIARY : ICON_BUTTON_TYPE.SECONDARY}
                    className='right-arrow-icon'
                    type='rounded'
                    onClick={() => onChangePage('Forward')}
                  />
                </ArrowBtn>
              </RightDiv>
            </Pagination>
          )}
        </Group>
      </AppLayout>
      <Modal open={isOpen} onClose={handleClose} maxWidth={'380px'}>
        <AddInvoiceModal closeModel={handleClose} />
      </Modal>
      <Modal open={deleteModelOpen} onClose={closeDeleteModel} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeDeleteModel}
          onOk={() => {
            if (item) onClickDeleteInvoice(item);
          }}
          loading={loading}
          modalTitle='Delete Invoice?'
          description='Are you sure to want to delete this invoice?'
        />
      </Modal>
      <BottomSubNavigation
        rightElement={
          <BottomNavigationItem
            iconName={
              filteredInvoices?.length > 0 && customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS && (isAdmin || isOwner)
                ? 'plus-icon'
                : ''
            }
            onClick={handleClick}
          />
        }
      />
    </>
  );
};

export default observer(Invoices);
